import React, {
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Alert,
  DialogContentText,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ActionButtonConfirmStatus } from "components";
import { api } from "api/api";
import { useTypedSelector } from "store";
import { getValidations } from "store/reducers/products/validation/validation.action";
import { ProcessState } from "types/process";
import { ValidatorInfo } from "types/validation";

const isSuccessStatus = (status: number): boolean =>
  status >= 200 && status < 300;

const getErrorMessage = (
  response: AxiosResponse | undefined,
  fallback: string
): string => {
  const data = response?.data;
  if (typeof data === "string" && data.trim()) {
    return data;
  }
  if (
    data &&
    typeof data === "object" &&
    "message" in data &&
    typeof (data as { message?: unknown }).message === "string"
  ) {
    return (data as { message: string }).message;
  }
  return fallback;
};

const namesMatch = (left: string, right: string): boolean =>
  left.toLowerCase() === right.toLowerCase();

export const ValidationSettingsButton: React.FC<{
  disabled?: boolean;
}> = ({ disabled = false }) => {
  const dispatch = useDispatch();
  const loadIdRef = useRef(0);

  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id ?? ""
  );

  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [toggleableValidators, setToggleableValidators] = useState<
    ValidatorInfo[]
  >([]);
  const [disabledNames, setDisabledNames] = useState<string[]>([]);

  const isNameDisabled = useCallback(
    (name: string) =>
      disabledNames.some((disabled) => namesMatch(disabled, name)),
    [disabledNames]
  );

  const handleToggle = useCallback((name: string, enabled: boolean) => {
    setDisabledNames((prev) => {
      const without = prev.filter((disabled) => !namesMatch(disabled, name));
      return enabled ? without : [...without, name];
    });
  }, []);

  const handleDialogOpen = useCallback(() => {
    const loadId = ++loadIdRef.current;
    setToggleableValidators([]);
    setDisabledNames([]);
    setProcessState(ProcessState.INITIALIZING);
    setStatusMessage("Loading validation settings...");

    if (!productLineId) {
      setProcessState(ProcessState.ERROR);
      setStatusMessage("No product line selected.");
      return;
    }

    void (async () => {
      const response = await api.getValidationSettings(productLineId);
      if (loadId !== loadIdRef.current) {
        return;
      }

      if (
        !isSuccessStatus(response.status) ||
        !Array.isArray(response.data?.validators)
      ) {
        setProcessState(ProcessState.ERROR);
        setStatusMessage(
          getErrorMessage(response, "Unable to load validation settings.")
        );
        return;
      }

      setToggleableValidators(
        response.data.validators.filter((validator) => validator.canDisable)
      );
      setDisabledNames(response.data.disabledValidatorNames ?? []);
      setProcessState(ProcessState.IDLE);
      setStatusMessage("");
    })();
  }, [productLineId]);

  const handleSave = async () => {
    if (!productLineId) {
      setProcessState(ProcessState.ERROR);
      setStatusMessage("No product line selected.");
      return;
    }

    setProcessState(ProcessState.PROCESSING);
    setStatusMessage("Saving validation settings...");

    const response = await api.updateValidationSettings(productLineId, {
      disabledValidatorNames: disabledNames,
    });

    if (!isSuccessStatus(response.status)) {
      const msg = getErrorMessage(
        response,
        "Unable to save validation settings."
      );
      setProcessState(ProcessState.ERROR);
      setStatusMessage(msg);
      return;
    }

    setDisabledNames(response.data?.disabledValidatorNames ?? disabledNames);
    try {
      await dispatch(getValidations());
    } catch {
      // Settings already saved; issue list refresh is best-effort.
    }

    const msg =
      "Validation settings saved. Existing issues for disabled checks were deleted.";
    toast.success(msg);
    setProcessState(ProcessState.COMPLETE);
    setStatusMessage(msg);
  };

  const contentSection = useMemo<ReactNode>(() => {
    if (
      processState !== ProcessState.IDLE &&
      processState !== ProcessState.PROCESSING
    ) {
      return undefined;
    }

    return (
      <Stack spacing={2}>
        <DialogContentText>
          Turn off a WARNING or INFO validation check for this product line.
        </DialogContentText>
        <Alert variant="outlined" severity="warning">
          Saving immediately deletes existing issues for any check you disable.
        </Alert>
        <Alert variant="outlined" severity="info">
          ERROR checks are mandatory and cannot be turned off.
        </Alert>
        {toggleableValidators.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No disableable validation checks are registered.
          </Typography>
        ) : (
          toggleableValidators.map((validator) => {
            const enabled = !isNameDisabled(validator.name);
            return (
              <FormControlLabel
                key={validator.name}
                sx={{ alignItems: "flex-start", ml: 0 }}
                control={
                  <Switch
                    checked={enabled}
                    onChange={(event) =>
                      handleToggle(validator.name, event.target.checked)
                    }
                    disabled={processState === ProcessState.PROCESSING}
                  />
                }
                label={
                  <Stack sx={{ pl: 1 }}>
                    <Typography variant="body1">
                      {validator.displayName || validator.name}
                    </Typography>
                    {validator.description && (
                      <Typography variant="body2" color="text.secondary">
                        {validator.description}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            );
          })
        )}
      </Stack>
    );
  }, [handleToggle, isNameDisabled, processState, toggleableValidators]);

  const inProgressTitle =
    processState === ProcessState.INITIALIZING
      ? "Loading settings"
      : "Saving settings";

  return (
    <ActionButtonConfirmStatus
      variant="outlined"
      color="primary"
      disabled={disabled || !productLineId}
      size="medium"
      icon={SettingsOutlinedIcon}
      iconOnly={false}
      tooltipText="Configure which WARNING and INFO validation checks run for this product line."
      tooltipArrow
      tooltipPlacement="right"
      maxWidth="sm"
      fullWidth
      dialogTitle="Validation Settings"
      onDialogOpen={handleDialogOpen}
      disableEscapeKeyDown
      disableCloseOnBackdropClick
      contentSection={contentSection}
      hideContentOnProcessComplete
      defaultButton="CANCEL"
      cancelButtonText="Cancel"
      okButtonText="Save"
      onOKButtonClick={handleSave}
      processState={processState}
      statusMessage={statusMessage}
      inProgressTitle={inProgressTitle}
      successTitle="Settings saved"
      errorTitle="Unable to update settings"
    >
      Validation Settings
    </ActionButtonConfirmStatus>
  );
};
